import { useContext, useEffect, useRef } from "react";
import { Navigate } from "react-router-dom";
import { getAuth } from "firebase/auth";
import { Box, Button, Link, Stack, Typography } from "@mui/material";
import { SiteNav } from "./marketingNav/SiteNav";
import { AuthContext } from "./auth/Auth";
import { useLoginState } from "./studioxr/auth/LoginState";
import { linkFromSignInUrl, useLinkSignIn } from "./Home/homeGateRedirect";
import { RoundMaterialCarousel } from "./design_stream/components/MaterialCarousel";

// First-party marketing homepage (issue #3829). This page must keep real,
// crawlable text and <a href> links in the DOM — replacing it with an iframe
// of the blog left mattoboard.com/ content-empty to Google and cost the
// homepage its head-term rankings after the May 2026 core update.

const GRADIENT =
  "linear-gradient(89.95deg, #5B47F5 1.57%, #B782FF 50.91%, #D0B1FE 98.39%)";

const FEATURES = [
  {
    title: "3D material sampling",
    body: "Curate physical materials as VSamples® — high-fidelity 3D swatches with realistic light and shadow — instead of flat images, and see how finishes actually behave together.",
  },
  {
    title: "Real products from real brands",
    body: "Drag and drop from a library of thousands of real materials, finishes, paints and furnishings, so every board is made of products you can specify and order.",
  },
  {
    title: "DesignStream AI concepts",
    body: "Generate interior design concepts from a brief — grounded in real, specifiable materials rather than imaginary AI renders — then iterate with material swaps and edits.",
  },
  {
    title: "FF&E schedules",
    body: "Turn a curated board into an FF&E schedule with pricing, dimensions and suppliers, and export it to PDF or Excel for clients and procurement.",
  },
];

const AUDIENCES = [
  { label: "For interior designers", href: "/designstream-designers" },
  { label: "For brands", href: "/designstream-brands" },
  { label: "For homeowners", href: "/designstream-homeowners" },
  { label: "For renovators", href: "/designstream-renovators" },
  { label: "Blog & guides", href: "https://blog.mattoboard.com/" },
];

const Splash = () => {
  const setShowLogin = useLoginState((state) => state.setShowLogin);

  const handleGetStarted = () => {
    sessionStorage?.setItem("redirectTo", "/home/concepts/new");
    setShowLogin("signup");
  };

  return (
    <div
      style={{
        background: "white",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <SiteNav />
      <Box
        component="main"
        sx={{
          flex: 1,
          minHeight: 0,
          pt: 1,
          pb: 10,
          px: 3,
          overflow: "auto",
          overflowX: "hidden",
          boxSizing: "border-box",
        }}
      >
        {/* Hero */}
        <Stack
          spacing={3}
          sx={{
            alignItems: "center",
            textAlign: "center",
            maxWidth: "820px",
            mx: "auto",
            mt: 3,
            mb: 5,
          }}
        >
          <Typography
            component="h1"
            sx={{
              fontSize: { xs: "2.4rem", md: "3.6rem" },
              fontWeight: 600,
              color: "#1a1a1a",
              lineHeight: 1.15,
            }}
          >
            The 3D mood board maker for interior designers
          </Typography>
          <Typography
            sx={{
              color: "#1a1a1a",
              fontSize: { xs: "0.95rem", md: "1.1rem" },
              lineHeight: 1.6,
              maxWidth: "640px",
            }}
          >
            Create interactive mood boards and material boards with real
            products from real brands — then generate AI interior concepts with{" "}
            <b>
              DesignStream{" "}
              <span
                style={{
                  background: GRADIENT,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                AI
              </span>
            </b>{" "}
            and build FF&amp;E schedules from what you curate.
          </Typography>
          <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleGetStarted}
              sx={{
                px: 6,
                py: 1.2,
                fontSize: "0.9rem",
                fontWeight: 800,
                background: GRADIENT,
                color: "white",
                textTransform: "none",
                letterSpacing: "0.5px",
                borderRadius: "50px",
                boxShadow: "none",
                "&:hover": {
                  background:
                    "linear-gradient(89.95deg, #4A38D9 1.57%, #9F6FE6 50.91%, #C09FED 98.39%)",
                  boxShadow: "none",
                },
              }}
            >
              Start Designing — It&apos;s Free
            </Button>
            <Link
              href="/designstream-designers"
              underline="hover"
              sx={{ color: "#5B47F5", fontWeight: 600, fontSize: "0.9rem" }}
            >
              Explore DesignStream AI
            </Link>
          </Stack>
        </Stack>

        {/* Live material carousel */}
        <RoundMaterialCarousel />

        {/* Features */}
        <Box
          component="section"
          sx={{ maxWidth: "1040px", mx: "auto", mt: 10 }}
        >
          <Typography
            component="h2"
            sx={{
              fontSize: { xs: "1.5rem", md: "2rem" },
              fontWeight: 600,
              color: "#1a1a1a",
              textAlign: "center",
              mb: 4,
            }}
          >
            From mood board to material board to spec
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 3,
            }}
          >
            {FEATURES.map((f) => (
              <Stack
                key={f.title}
                spacing={1}
                sx={{
                  p: 3,
                  border: "1px solid #eee",
                  borderRadius: "12px",
                  background: "#fafafa",
                }}
              >
                <Typography
                  component="h3"
                  sx={{
                    fontSize: "1.05rem",
                    fontWeight: 700,
                    color: "#1a1a1a",
                  }}
                >
                  {f.title}
                </Typography>
                <Typography
                  sx={{ fontSize: "0.9rem", color: "#444", lineHeight: 1.6 }}
                >
                  {f.body}
                </Typography>
              </Stack>
            ))}
          </Box>
        </Box>

        {/* Visual */}
        <Box
          component="section"
          sx={{ maxWidth: "880px", mx: "auto", mt: 8, textAlign: "center" }}
        >
          <img
            src="/images/dashboard-hero-parisian.jpg"
            alt="Interior design mood board built in Mattoboard with real 3D material samples"
            loading="lazy"
            style={{ width: "100%", borderRadius: "12px", display: "block" }}
          />
        </Box>

        {/* Audiences / crawlable internal links */}
        <Stack
          component="nav"
          aria-label="Mattoboard for every audience"
          direction="row"
          spacing={3}
          useFlexGap
          sx={{ justifyContent: "center", flexWrap: "wrap", mt: 8 }}
        >
          {AUDIENCES.map((a) => (
            <Link
              key={a.href}
              href={a.href}
              underline="hover"
              sx={{ color: "#555", fontSize: "0.85rem", fontWeight: 600 }}
            >
              {a.label}
            </Link>
          ))}
        </Stack>
      </Box>
    </div>
  );
};

export function StartLoginDs() {
  const { currentUser } = useContext(AuthContext);
  const setShowLogin = useLoginState((state) => state.setShowLogin);
  const showLogin = useLoginState((state) => state.showLogin);
  const modalWasOpen = useRef(false);
  // First: stamps a return link before the modal opens (setShowLogin keeps
  // an existing stamp), and forgets it if this page is left signed out.
  useLinkSignIn();

  useEffect(() => {
    if (currentUser) return;
    // ?auth=signin: a return link (homeGateRedirect).
    const auth = new URLSearchParams(window.location.search).get("auth");
    setShowLogin(auth === "signin" ? "signin" : "signup");
  }, [currentUser, setShowLogin]);

  useEffect(() => {
    if (showLogin) {
      modalWasOpen.current = true;
      return;
    }
    // Modal dismissed without logging in: full page load back to / so the
    // Cloudflare Worker can re-serve the marketing page (an SPA Navigate
    // never hits the server). getAuth().currentUser, not the context — it is
    // already set when the auth forms close the modal on success, while the
    // context updates a beat later.
    if (modalWasOpen.current && !getAuth().currentUser)
      window.location.assign("/");
  }, [showLogin]);

  // Don't redirect while a signup/signin modal is active — let the modal flow finish.
  // A return link in the URL wins (e.g. the user signed in from another tab).
  if (currentUser && !showLogin) {
    const link = linkFromSignInUrl(window.location.search);
    return <Navigate to={link ?? "/home/library"} replace />;
  }
  return <Splash />;
}

export default Splash;
